import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { GOOGLE_DISAPPROVAL_THRESHOLD, googleDisapprovalRate } from '@/lib/google-disapproval';
import { DISCONNECTED_THRESHOLD_DAYS, daysSince } from '@/lib/connection-state';

// Surfaced on the Google channel's sync card because GMC's snapshot lags
// briefly behind our Typesense flags — retailers see the gap on the "Last sync"
// line and the GMC accepted/disapproved counts and assume the dashboard is
// wrong; this explains the cadence in retailer terms.
const GOOGLE_SYNC_LAG_NOTE =
	'Your stock is sent to Google in real time, and Google rebuilds its public catalogue on its own schedule — typically every few hours. Small differences between "Last sync" and what Google is showing right now are normal and resolve on its next refresh.';

/**
 * Current sync status for a live channel on its manage page.
 *
 * When `gmcProductStats` is supplied (Google channel), the card reflects
 * Google Merchant Centre's actual acceptance state rather than just the
 * internal sync state. If GMC has disapproved all products the card
 * switches from the green "Live" state to an amber "Issues" state. A small
 * info tooltip next to "Last sync" explains GMC's refresh cadence in
 * retailer terms so the brief gap between our sync and Google's snapshot
 * doesn't read as a bug.
 */
export function ChannelSyncStatus({
	channel,
	lastSync,
	lastSyncAt,
	activeItems,
	totalItems,
	gmcProductStats,
	liaServingState,
	liaAccountIssue = false
}) {
	const gmcActive = gmcProductStats?.active ?? null;
	const gmcDisapproved = gmcProductStats?.disapproved ?? 0;
	const gmcUnmatched = gmcProductStats?.unmatched ?? 0;
	// Local Ads only: Google's serving verdict for Local Inventory Ads
	// (`serving.state` from /api/local-ads, NEA-6127). While the review is
	// pending the card must not read "Live" — items sync fine, but nothing
	// shows to shoppers yet. A disapproved catalogue (`issues`) is actionable,
	// not a wait — it gets the "Issues" pill and fix-it guidance instead.
	const liaIssues = liaServingState === 'issues';
	const liaInReview = liaServingState != null && liaServingState !== 'live' && !liaIssues;
	// Card flips amber only when Google is rejecting a meaningful share of the
	// catalogue (NEA-5921: 30%+) — a single disapproval on a 400-item feed isn't
	// actionable. `allGmcDisapproved` stays as its own hard-fail signal: zero
	// products accepted is always a warning regardless of count.
	const hasGmcIssues = gmcActive !== null && gmcDisapproved > 0 && googleDisapprovalRate(gmcProductStats) >= GOOGLE_DISAPPROVAL_THRESHOLD;
	const allGmcDisapproved = gmcActive === 0 && gmcDisapproved > 0;
	// Show the GMC-flavoured stat row whenever Google reports something
	// non-trivial about the feed — either a rejection (disapproved) or items
	// it can't match against its catalogue (unmatched). When both are zero we
	// fall through to the generic "X of Y items syncing" line.
	const showGmcStats = gmcActive !== null && (gmcDisapproved > 0 || gmcUnmatched > 0);
	const emptyFeed = totalItems != null && totalItems === 0 && (activeItems ?? 0) === 0;
	const daysSinceLastSync = daysSince(lastSyncAt);
	const isStale = daysSinceLastSync != null && daysSinceLastSync >= DISCONNECTED_THRESHOLD_DAYS;
	const hasWarning = hasGmcIssues || allGmcDisapproved || emptyFeed || isStale || liaInReview || liaIssues;

	const StatusIcon = hasWarning ? AlertTriangle : CheckCircle2;
	const iconBg = hasWarning ? 'bg-warning-soft' : 'bg-success-soft';
	const iconColor = hasWarning ? 'text-warning-soft-foreground' : 'text-success-soft-foreground';
	const pillBg = hasWarning ? 'bg-warning-soft' : 'bg-success-soft';
	const pillColor = hasWarning ? 'text-warning-soft-foreground' : 'text-success-soft-foreground';
	const pillLabel =
		allGmcDisapproved || emptyFeed ? 'No products' : liaIssues ? 'Issues' : liaInReview ? 'In review' : isStale ? 'Issues' : 'Live';
	// Only Google has the GMC-snapshot lag worth explaining — every other
	// channel reads our own sync timestamp directly.
	const showSyncLagTooltip = gmcProductStats != null;

	return (
		<SoftCard className="!p-5 md:!p-6">
			<TooltipProvider delayDuration={150}>
				<div className="flex items-start gap-4">
					<div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${iconBg}`}>
						<StatusIcon className={`h-5 w-5 ${iconColor}`} />
					</div>
					<div className="min-w-0 flex-1">
						<div className="flex flex-wrap items-center gap-2">
							<h3 className="text-[15px] font-bold text-foreground md:text-base">Connected to {channel.title}</h3>
							<span
								className={`inline-flex items-center gap-1 rounded-full ${pillBg} px-2 py-0.5 text-[11px] font-semibold ${pillColor}`}
							>
								{pillLabel}
							</span>
						</div>
						{lastSync ? (
							<div className="mt-1 inline-flex items-center gap-1 text-xs tabular-nums text-muted-foreground">
								<span>Last sync · {lastSync}</span>
								{showSyncLagTooltip ? (
									<Tooltip>
										<TooltipTrigger asChild>
											<button
												type="button"
												className="text-muted-foreground transition hover:text-foreground"
												aria-label="Why does this look slightly out of date?"
											>
												<Info className="h-3.5 w-3.5" />
											</button>
										</TooltipTrigger>
										<TooltipContent className="max-w-xs text-xs leading-relaxed">{GOOGLE_SYNC_LAG_NOTE}</TooltipContent>
									</Tooltip>
								) : null}
							</div>
						) : null}
						{allGmcDisapproved ? (
							<div className="mt-1 text-xs text-warning-soft-foreground">
								<span className="font-semibold">{gmcDisapproved.toLocaleString('en-GB')}</span> products disapproved by
								Google — none showing to shoppers. Contact support@unify.near.st for help.
							</div>
						) : liaIssues ? (
							<div className="mt-1 text-xs text-warning-soft-foreground">
								{liaAccountIssue
									? "Google has restricted this store's Merchant Center account — ads won't show until it's resolved. Contact support@unify.near.st for help."
									: "Google has disapproved your products for Local Inventory Ads — ads won't show until they're fixed. Contact support@unify.near.st for help."}
							</div>
						) : liaInReview ? (
							<div className="mt-1 text-xs text-warning-soft-foreground">
								Google is reviewing your store for Local Inventory Ads — ads don&rsquo;t show until the review completes.
							</div>
						) : isStale ? (
							<div className="mt-1 text-xs text-warning-soft-foreground">
								Inventory hasn&rsquo;t synced in {daysSinceLastSync} days — check your POS connection.
							</div>
						) : showGmcStats ? (
							<div className="mt-1 text-xs tabular-nums text-muted-foreground">
								<span className="font-semibold text-foreground">{gmcActive.toLocaleString('en-GB')}</span> accepted
								{gmcDisapproved > 0 ? (
									<>
										{' · '}
										<span className="font-semibold text-warning-soft-foreground">
											{gmcDisapproved.toLocaleString('en-GB')}
										</span>{' '}
										disapproved by Google
									</>
								) : null}
								{gmcUnmatched > 0 ? (
									<>
										{' · '}
										<span className="font-semibold text-foreground">{gmcUnmatched.toLocaleString('en-GB')}</span> not in
										Google&rsquo;s catalogue{' '}
										<Tooltip>
											<TooltipTrigger asChild>
												<button
													type="button"
													aria-label="What does 'not in Google's catalogue' mean?"
													className="inline-flex items-baseline align-middle text-muted-foreground hover:text-foreground"
												>
													<Info className="h-3 w-3" />
												</button>
											</TooltipTrigger>
											<TooltipContent side="top" className="max-w-[280px] text-left leading-snug">
												Google can only list products it has in its own catalogue. Items not in its catalogue
												aren&rsquo;t disapproved &mdash; Google just doesn&rsquo;t have a match yet. You can add
												your own product data to Unify to get them listed from the Inventory tab.
											</TooltipContent>
										</Tooltip>
									</>
								) : null}
							</div>
						) : emptyFeed ? (
							<div className="mt-1 text-xs text-warning-soft-foreground">
								No products are being sent to {channel.title} — check your inventory connection.
							</div>
						) : totalItems != null ? (
							<div className="mt-1 text-xs tabular-nums text-muted-foreground">
								<span className="font-semibold text-foreground">{(activeItems ?? totalItems).toLocaleString('en-GB')}</span>{' '}
								of {totalItems.toLocaleString('en-GB')} items syncing
							</div>
						) : (
							<p className="mt-1 text-sm text-muted-foreground">
								Your in-store stock is syncing to {channel.title} automatically — no manual uploads.
							</p>
						)}
					</div>
				</div>
			</TooltipProvider>
		</SoftCard>
	);
}
